import React, { useState } from 'react';
import { arrayOf, bool, shape, string, object, func } from 'prop-types';
import moment from '@narvar/moment-shim';
import { connect } from 'react-redux';
import {
  ActionBar,
  ActionButton,
  CardToggle,
  DetailFieldsWrapper,
  DetailFieldTitle,
  DetailFieldValue,
  Header,
  ReturnCardContentWrapper,
  ReturnOrderWrapper,
} from './styles';
import { ItemCard } from '../ItemCard';
import { itemProps, returnLabelProps } from './proptypes';

const ReturnCardContent = ({ returnItems }) => (
  <ReturnCardContentWrapper>
    {returnItems.map((item, itemIndex) => (
      <ItemCard {...{ itemIndex, item, key: item.item_id, initiated: true }} />
    ))}
  </ReturnCardContentWrapper>
);

const Toggle = ({ opened }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" width="18">
    {opened ? (
      <path d="M16.5 102 2 87.6 64 26l62 61.6-14.5 14.4L64 54.8 16.5 102z" />
    ) : (
      <path d="M111.5 26 126 40.4 64 102 2 40.4 16.5 26 64 73.2 111.5 26z" />
    )}
  </svg>
);

const mapStateToProps = ({
  language: lang,
  language: {
    'returns.step_1.reprint_label_btn': reprintLabel,
    'returns.step_1.track_return_btn': trackLabel,
  } = {},
}) => ({
  reprintLabel,
  trackLabel,
  lang,
});

const DetailField = ({ title, value }) => (
  <DetailFieldValue>
    <DetailFieldTitle>{title}</DetailFieldTitle>
    <DetailFieldValue>{value}</DetailFieldValue>
  </DetailFieldValue>
);

const calculateValue = ({ key, value }) => {
  if (['created_date'].includes(key)) {
    return moment(value).format('dddd, LL');
  }
  return value;
};

const DetailFields = ({ fields, lang, onClick }) => (
  <DetailFieldsWrapper onClick={onClick}>
    {fields.map(({ key, value }) => (
      <DetailField
        title={lang[`returns.step_1.item_level_grouping.my_returns.return.detail_field.${key}`]}
        value={calculateValue({ key, value })}
        key={key}
      />
    ))}
  </DetailFieldsWrapper>
);

export const ReturnCardComponent = ({
  label,
  label: { return_items: returnItems },
  handlers: { printLabel },
  flags: { showButtonTrack, showButtonReprintLabel, isNotShipped },
  reprintLabel,
  trackLabel,
  lang,
}) => {
  const [opened, setOpened] = useState(false);

  const handleToggle = () => {
    setOpened(!opened);
  };

  const handlePrintLabel = () => {
    printLabel(label, {
      preventDefault: () => {},
    });
  };

  const generateActions = () => {
    const actions = [];

    // Not Shipped
    if (isNotShipped(label)) {
      if (showButtonReprintLabel(label)) {
        actions.push(
          <ActionButton key="reprintLabel" onClick={handlePrintLabel}>
            {reprintLabel}
          </ActionButton>,
        );
      }
      /* Uncomment when cancel has been added to redux actions
        if (showButtonCancel(label)) {
          actions.push(<ActionButton onClick={handleCancelLabel}>Cancel Return</ActionButton>);
        }
      */
    }

    // Shipped
    if (!isNotShipped(label)) {
      if (showButtonTrack(label)) {
        actions.push(
          <ActionButton key="trackLabel">
            <a href={label.tracking_url} target="_blank" rel="noopener noreferrer">
              {trackLabel}
            </a>
          </ActionButton>,
        );
      }
    }

    return actions;
  };

  return (
    <ReturnOrderWrapper>
      <Header className="my-returns-card-header" opened={opened}>
        <DetailFields
          fields={[{ key: 'created_date', value: label.created_date }]}
          lang={lang}
          onClick={handleToggle}
          className="my-returns-detail-fields"
        />
        <ActionBar>{generateActions()}</ActionBar>
        <CardToggle onClick={handleToggle}>
          <Toggle opened={opened} />
        </CardToggle>
      </Header>
      {opened && <ReturnCardContent {...{ returnItems }} />}
    </ReturnOrderWrapper>
  );
};

export const ReturnCard = connect(mapStateToProps)(ReturnCardComponent);

ReturnCardComponent.propTypes = returnLabelProps;

ReturnCardContent.propTypes = {
  returnItems: arrayOf(shape(itemProps)),
};

ReturnCardContent.defaultProps = {
  returnItems: [],
};

Toggle.propTypes = {
  opened: bool.isRequired,
};

DetailField.propTypes = {
  title: string.isRequired,
  value: string.isRequired,
};

DetailFields.propTypes = {
  fields: arrayOf(object).isRequired,
  lang: object.isRequired,
  onClick: func.isRequired,
};
